import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wallet, Bell, ChevronRight, Sun, Moon, MapPin, Heart, Eye,
  Sparkles, PauseCircle, Plus, Truck, LogIn, ShoppingBag, Clock, Search, ShoppingCart, ChevronDown,
  AlertTriangle,
} from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
// formatClock / formatSlotWindow live in lib/utils.fmt.js so the cut-off
// ribbon, the Next-delivery card and any future surface render an
// admin-configured window identically — and so they're unit-testable
// without a React renderer.
import { formatINR, formatClock, formatSlotWindow } from "../../lib/utils.fmt";
import { onAddressChanged } from "../../lib/addressBus";
import { useT } from "../../lib/i18n";
import { getTenantSlug } from "../../lib/tenant";
import { useCart } from "../../lib/cart";
import usePushRegistration from "../../lib/usePushRegistration";
import { personaForRole } from "../../lib/pushNotifications";
import CustomerHeader from "../../components/CustomerHeader";
import ProductCard from "../../components/ProductCard";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import { todayIST } from "../../lib/istDate";

export default function CustomerHome() {
  const { user, tenant: authTenant } = useAuth();
  const { t } = useT();
  const { count: cartCount } = useCart();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [activeCatId, setActiveCatId] = useState(null);  // scroll-spy active category
  const circlesRef = useRef(null);                       // for auto-scrolling active circle into view

  // Capacitor native apps: ask for push permission + register FCM token (no-op on web)
  // Persona follows the logged-in ROLE, not this screen. Previously gated
  // on `!!user`, so an admin browsing the customer side relabelled their
  // only device as "customer" and stopped receiving admin alerts.
  usePushRegistration(personaForRole(user?.role), !!user?.role);

  const loadHome = () => {
    setLoadError(null);
    if (user) {
      api.get("/api/customer/home").then(({ data }) => setData(data))
        .catch((e) => setLoadError(friendlyMessage(e)));
      api.get("/api/customer/addresses").then(({ data }) => setAddresses(data.addresses || [])).catch(() => {});
    } else {
      const slug = getTenantSlug();
      api.get(`/api/public/tenant/${slug}/home`).then(({ data }) => {
        setData({
          wallet: null,
          active_subscriptions: 0,
          today_deliveries: [],
          tomorrow_deliveries: [],
          featured_products: data.featured_products,
          products: data.products,
          categories: data.categories,
          banners: data.banners,
          brands: data.brands,
          reels: data.reels,
          tenant: data.tenant,
        });
      }).catch((e) => setLoadError(friendlyMessage(e)));
    }
  };
  useEffect(() => { loadHome(); /* eslint-disable-next-line */ }, [user]);

  // The header's "Delivery to" picker can change which address is active
  // while Home is already mounted. Re-read just the addresses so the
  // serviceability banner and the address-driven copy follow immediately —
  // without re-fetching the whole Home payload (products, banners, reels).
  useEffect(() => {
    if (!user) return undefined;
    return onAddressChanged(() => {
      api.get("/api/customer/addresses")
        .then(({ data }) => setAddresses(data.addresses || []))
        .catch(() => {});
    });
  }, [user]);

  // Subcategories grouped by their parent's id — only categories with
  // parent_id set show up here, so a tenant with no hierarchy gets {}.
  const subcatsByParent = useMemo(() => {
    const groups = {};
    for (const c of data?.categories || []) {
      if (c.parent_id) (groups[c.parent_id] = groups[c.parent_id] || []).push(c);
    }
    return groups;
  }, [data?.categories]);

  // Group products by their TOP-LEVEL category — a product tagged to a
  // subcategory rolls up into its parent's strip (rather than becoming its
  // own separate one-subcategory strip), same as how Products.jsx's "All
  // {category}" view works. For any category with no parent (or a tenant
  // with no hierarchy at all), this is identical to grouping by
  // category_id directly — zero behavior change for them.
  const productsByCategory = useMemo(() => {
    if (!data?.products) return {};
    const catById = new Map((data.categories || []).map((c) => [c.id, c]));
    const groups = {};
    for (const p of data.products) {
      const cat = p.category_id ? catById.get(p.category_id) : null;
      const topId = cat ? (cat.parent_id || cat.id) : (p.category_id || "_uncategorized");
      (groups[topId] = groups[topId] || []).push(p);
    }
    return groups;
  }, [data?.products, data?.categories]);

  // Top-level categories with at least one product (directly or via a
  // subcategory) — subcategories never get their own circle/strip.
  const cats = useMemo(
    () => (data?.categories || []).filter((c) => !c.parent_id && productsByCategory[c.id]?.length),
    [data?.categories, productsByCategory]
  );

  // Scroll-spy: observe each category strip and update activeCatId
  useEffect(() => {
    if (!cats.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Among entries currently intersecting, pick the one closest to the top
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].target.id.replace(/^cat-/, "");
          const matched = cats.find((c) => c.slug === id);
          if (matched) setActiveCatId(matched.id);
        }
      },
      // top margin approximates the sticky header + circles strip height
      { rootMargin: "-180px 0px -55% 0px", threshold: 0 }
    );
    cats.forEach((c) => {
      const el = document.getElementById(`cat-${c.slug}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [cats]);

  // Auto-scroll the active circle into view in the horizontal strip
  useEffect(() => {
    if (!activeCatId || !circlesRef.current) return;
    const btn = circlesRef.current.querySelector(`[data-cat-id="${activeCatId}"]`);
    if (btn) btn.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeCatId]);

  if (loadError && !data) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load the store" description={loadError}
        action={<button onClick={loadHome} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight className="min-h-screen" testId="customer-home-error" />
    );
  }
  if (!data) return <LoadingState fullHeight className="min-h-screen" testId="customer-home-loading" />;

  const tenant = authTenant || data.tenant;
  const isGuest = !user;
  const wallet = data.wallet?.balance ?? 0;
  const lowWallet = wallet < 200;
  const primary = tenant?.branding?.primary_color || "#3A86FF";
  const secondary = tenant?.branding?.secondary_color || "#81B29A";
  const banners = (data.banners || []).filter((b) => b.is_active !== false);

  // Delivery windows (tenant-configured, multi-slot) — used for the cutoff ribbon below.
  const allWindows = (tenant?.delivery_windows && tenant.delivery_windows.length
                       ? tenant.delivery_windows
                       : tenant?.delivery_window  // legacy single
                         ? [tenant.delivery_window]
                         : []).filter((w) => w.is_active !== false);
  const cutoffState = pickActiveSlot(allWindows);
  const nextDelivery = data.tomorrow_deliveries?.[0];
  // The customer's active delivery address (default, or first saved one) —
  // surfaced here so an out-of-range location is flagged the moment the
  // customer lands on Home, not only after they've filled out an order and
  // hit Place Order on Checkout. `serviceable` is hydrated server-side by
  // GET /api/customer/addresses (delivery_validation.check_address_serviceable).
  const defaultAddress = addresses.find((a) => a.is_default) || addresses[0] || null;
  const homeAddressUnserviceable = !isGuest && defaultAddress?.serviceable === false;

  /** Find this customer's actual upcoming delivery's slot times (not the
   *  tenant-wide cutoff slot — those can differ).
   */
  const nextDeliveryWindow = nextDelivery
    ? allWindows.find((w) => (w.label || "").toLowerCase() === (nextDelivery.slot || "").toLowerCase())
    : null;

  /** Convert an ISO date to a friendly "Today" / "Tomorrow" / "Mon 16 Jun" label. */
  const friendlyDate = (iso) => {
    if (!iso) return "";
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
    const target = new Date(y, (m || 1) - 1, d || 1);
    if (target.getTime() === today.getTime()) return "Today";
    if (target.getTime() === tomorrow.getTime()) return "Tomorrow";
    return target.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  };

  return (
    <div className="page-enter" data-testid="customer-home-page">
      <CustomerHeader primary={primary}/>

      <main className="px-5 space-y-6 pb-8">
        {/* Location-not-serviceable banner — shown as soon as the customer's
            active delivery address is known to be outside the tenant's
            configured pincode list / delivery radius. */}
        {homeAddressUnserviceable && (
          <div className="rounded-2xl border-2 border-brand-clay/40 bg-brand-clay/10 px-4 py-3 flex items-start gap-3"
               data-testid="home-unserviceable-banner">
            <div className="w-9 h-9 rounded-xl bg-brand-clay flex items-center justify-center shrink-0 text-white">
              <MapPin className="w-4 h-4"/>
            </div>
            <div className="text-sm leading-snug flex-1 min-w-0">
              <div className="font-extrabold text-brand-clay">Location Not Serviceable</div>
              <div className="text-neutral-700 mt-0.5">Sorry, we currently don&rsquo;t deliver to this location. Please select a different delivery address.</div>
              <Link to="/app/addresses" state={{ from: "/app" }} className="inline-block mt-1.5 text-xs font-bold text-brand-clay underline" data-testid="home-unserviceable-change-address">
                Change delivery address
              </Link>
            </div>
          </div>
        )}

        {/* Cut-off ribbon (dynamic: switches to "day after" once past last cut-off) */}
        {cutoffState && (
          <div className="rounded-2xl border-2 px-4 py-3 flex items-start gap-3"
               style={{ borderColor: `${primary}55`, background: `${primary}0F` }}
               data-testid="cutoff-ribbon">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: primary, color: "#fff" }}>
              <Clock className="w-4 h-4"/>
            </div>
            <div className="text-sm leading-snug">
              {cutoffState.message}
            </div>
          </div>
        )}

        {/* Next-delivery ribbon (only when there's a scheduled delivery).
            The full breakdown (items + slot) lives in the dedicated
            "Tomorrow's delivery" card below the wallet, so the banner just
            shows the timing here — no need to repeat product names. */}
        {!isGuest && nextDelivery && (
          <Link to="/app/next-delivery" className="block" data-testid="next-delivery-ribbon">
            <div className="rounded-2xl text-white px-4 py-3 flex items-center gap-3"
                 style={{ background: `linear-gradient(90deg, ${primary} 0%, ${secondary} 100%)` }}>
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                <Truck className="w-5 h-5"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider font-bold text-white/85">
                  Next delivery
                </div>
                {/* Was `truncate`: the window is the point of this card, and
                    on a phone the single line clipped mid-range to
                    "Morning 6:00 AM – 10:…", hiding the very number the
                    customer opened it for. Wrap instead of clipping. */}
                <div className="font-heading font-extrabold" data-testid="next-delivery-label">
                  {friendlyDate(nextDelivery.delivery_date)}
                  {nextDeliveryWindow ? ` · ${nextDeliveryWindow.label} ${formatSlotWindow(nextDeliveryWindow)}` : ""}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 shrink-0"/>
            </div>
          </Link>
        )}

        {/* Banners carousel */}
        {banners.length > 0 ? (
          <BannerCarousel banners={banners} primary={primary} secondary={secondary}/>
        ) : isGuest ? (
          <div className="rounded-3xl text-white p-6 relative overflow-hidden"
               style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}
               data-testid="guest-hero">
            <div className="absolute -right-10 -top-10 w-44 h-44 bg-white/15 blur-2xl rounded-full" />
            <div className="relative">
              <div className="text-xs uppercase tracking-wider font-bold text-white/85 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Welcome to {tenant?.name || "your dairy"}
              </div>
              <div className="font-heading text-3xl font-extrabold mt-2 tracking-tight leading-tight">
                Farm-fresh milk,<br/>at your doorstep.
              </div>
              <p className="text-sm text-white/85 mt-3">Browse our catalog · subscribe in 30 seconds.</p>
              <Link to="/login" data-testid="hero-signin-btn">
                <button className="mt-5 bg-white rounded-2xl h-12 px-5 font-bold text-sm inline-flex items-center gap-1.5"
                        style={{ color: primary }}>
                  <LogIn className="w-4 h-4" /> Sign in to subscribe
                </button>
              </Link>
            </div>
          </div>
        ) : null}

        {/* Trial CTA — first-time-only invitation to try a short N-day
            subscription. Free or paid depending on tenant.trial_billing. */}
        {!isGuest && data?.trial?.eligible && (
          <Link to="/app/products" className="block" data-testid="trial-cta-banner">
            <div className="rounded-3xl bg-gradient-to-br from-brand-leaf to-brand-sky text-white p-5 relative overflow-hidden">
              <div className="absolute -right-6 -top-6 w-32 h-32 bg-white/10 blur-2xl rounded-full" />
              <div className="relative flex items-start gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                  <Sparkles className="w-6 h-6"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-white/85">
                    {data.trial.is_free ? `Try free · ${data.trial.days} days` : `${data.trial.days}-day trial`}
                  </div>
                  <div className="font-heading text-lg sm:text-xl font-extrabold leading-tight mt-0.5">
                    {data.trial.is_free
                      ? `Subscribe and your first ${data.trial.days} days are on us.`
                      : `Try us for ${data.trial.days} days — no long-term commitment.`}
                  </div>
                  <div className="text-xs text-white/85 mt-1">
                    {data.trial.is_free
                      ? "No wallet charge during trial · cancel anytime."
                      : "Pay per delivery, auto-cancels after the trial · then re-subscribe to keep going."}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 shrink-0 mt-1"/>
              </div>
            </div>
          </Link>
        )}

        {/* Wallet card (logged-in only, after banners) */}
        {!isGuest && (
          <Link to="/app/wallet" className="block" data-testid="wallet-card">
            <div className={`rounded-3xl p-5 ${lowWallet ? "bg-brand-clay/10 border border-brand-clay/30" : "text-white"}`}
                 style={!lowWallet ? { background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` } : {}}>
              <div className="flex items-start justify-between">
                <div>
                  <div className={`text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 ${lowWallet ? "text-brand-clay" : "text-white/85"}`}>
                    <Wallet className="w-3.5 h-3.5" /> {t("wallet_balance")}
                  </div>
                  <div className={`font-heading text-4xl font-extrabold mt-2 tracking-tight ${lowWallet ? "text-brand-clay" : "text-white"}`}>
                    {formatINR(wallet)}
                  </div>
                  {lowWallet && <div className="text-xs text-brand-clay font-semibold mt-2">⚠ {t("low_balance")}</div>}
                </div>
                <button className={`rounded-2xl px-4 h-11 font-bold text-sm flex items-center gap-1.5 ${lowWallet ? "bg-brand-clay text-white" : "bg-white"}`}
                        style={!lowWallet ? { color: primary } : {}}
                        data-testid="recharge-btn">
                  <Plus className="w-4 h-4" /> {t("recharge")}
                </button>
              </div>
            </div>
          </Link>
        )}

        {/* Next delivery — grouped by (product, slot) so 5 same-day same-slot
            subs collapse into one tidy line. The customer can still tap
            "Manage subs" below to drill into each individual subscription. */}
        {!isGuest && data.tomorrow_deliveries?.length > 0 && (() => {
          const rows = data.tomorrow_deliveries;
          // Group adjacent (product_id, slot, delivery_date) rows. Sum
          // quantity and amount; use the first row's unit price as the
          // representative price line.
          const grouped = [];
          const indexByKey = new Map();
          for (const r of rows) {
            const key = `${r.product_id}|${r.slot}|${r.delivery_date}`;
            if (indexByKey.has(key)) {
              const g = grouped[indexByKey.get(key)];
              g.qty += (r.quantity || 0);
              g.amount += (r.amount || 0);
              g.subCount += 1;
            } else {
              indexByKey.set(key, grouped.length);
              const unitPrice = r.quantity ? (r.amount || 0) / r.quantity : (r.amount || 0);
              grouped.push({
                key,
                product_name: r.product_name,
                slot: r.slot,
                delivery_date: r.delivery_date,
                qty: r.quantity || 0,
                amount: r.amount || 0,
                unit_price: unitPrice,
                subCount: 1,
              });
            }
          }
          // Header slot label — derive from the first row instead of hard-coding "5:30 – 7:30 AM".
          const firstDate = rows[0]?.delivery_date;
          const today = todayIST();
          const headerDay = firstDate === today ? t("today") : t("tomorrow_delivery");
          const headerSlot = (rows[0]?.slot || "").replace(/^[a-z]/, (c) => c.toUpperCase());
          return (
            <div className="rounded-3xl bg-white border border-neutral-200 p-5" data-testid="next-delivery-card">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5" style={{ color: primary }}>
                  <Truck className="w-3.5 h-3.5" /> {headerDay}
                </div>
                {headerSlot && <div className="text-xs text-neutral-500 font-semibold" data-testid="next-delivery-slot-label">{headerSlot}</div>}
              </div>
              <div className="space-y-2">
                {grouped.map((g) => (
                  <Link
                    to="/app/subscriptions"
                    key={g.key}
                    className="flex items-center justify-between py-2 border-b last:border-b-0 border-neutral-100 hover:bg-neutral-50 rounded-lg -mx-2 px-2 transition-colors"
                    data-testid={`delivery-group-${g.key}`}
                  >
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{g.product_name}</div>
                      <div className="text-xs text-neutral-500">
                        {g.subCount > 1
                          ? `${g.qty} × ${formatINR(g.unit_price)} · ${g.slot} · ${g.subCount} subs`
                          : `${g.slot} · Qty ${g.qty}`}
                      </div>
                    </div>
                    <div className="font-mono font-bold ml-3 shrink-0">{formatINR(g.amount)}</div>
                  </Link>
                ))}
              </div>
              <Link to="/app/subscriptions" className="mt-3 flex items-center text-sm font-bold gap-1" style={{ color: primary }} data-testid="manage-subs-link">
                {t("manage_subs")} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          );
        })()}

        {/* Today's status */}
        {!isGuest && data.today_deliveries?.length > 0 && (
          <div className="rounded-3xl bg-brand-leaf/10 border border-brand-leaf/30 p-5" data-testid="today-delivery-card">
            <div className="text-xs uppercase tracking-wider font-bold text-brand-leaf flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-brand-leaf animate-pulse"/> {t("today")}
            </div>
            {data.today_deliveries.map((d) => (
              <div key={d.id} className="mt-2 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{d.product_name} × {d.quantity}</div>
                  <div className="text-xs text-neutral-600 capitalize">{d.status.replace("_", " ")}</div>
                </div>
                <Link to={`/app/orders/${d.id}`} className="text-sm font-bold inline-flex items-center gap-1" style={{ color: primary }} data-testid={`track-${d.id}`}>
                  {t("track")} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* Sticky category circles with scroll-spy.
            Padding lives on the INNER scrollable div (not the section)
            so the active circle's ring-4 + ring-offset-2 + scale-110
            visual footprint (~88 px effective width) always fits
            comfortably even at scroll position 0 — otherwise the ring
            gets clipped by the strip's left edge on the very first
            item. `env(safe-area-inset-left/right)` honours the notch
            on iPhones held in landscape.

            The same footprint has to fit VERTICALLY too: setting
            `overflow-x-auto` while `overflow-y` is `visible` doesn't
            actually keep the y-axis visible — per the CSS overflow spec,
            when one axis is scrollable the other computes to `auto`
            instead, so it clips too. pt-3.5 on this div (not just the
            section's own py-4, which is a non-clipping ancestor) is
            sized to contain the active circle's ~10px ring+scale
            overflow so it never gets a flat-cropped top edge. The
            section's own top padding is intentionally minimal since the
            space-y-6 gap from the sibling above already separates them. */}
        {cats.length > 0 && (
          <section className="sticky z-20 -mx-5 pb-4 bg-brand-cream/95 backdrop-blur" data-testid="category-circles"
                   style={{ top: "calc(68px + env(safe-area-inset-top, 0px))" }}>
            <div
              ref={circlesRef}
              className="flex gap-4 overflow-x-auto overflow-y-visible no-scrollbar pt-3.5 pb-3"
              style={{
                paddingLeft: "max(1.5rem, env(safe-area-inset-left))",
                paddingRight: "max(1.5rem, env(safe-area-inset-right))",
                scrollPaddingLeft: "1.5rem",
                scrollPaddingRight: "1.5rem",
              }}
            >
              {cats.map((c) => (
                <CategoryCircle key={c.id} cat={c}
                                isActive={activeCatId === c.id}
                                primary={primary}/>
              ))}
            </div>
          </section>
        )}

        {/* Quick actions */}
        {!isGuest && (
          <div className="grid grid-cols-4 gap-3" data-testid="quick-actions">
            {[
              { icon: PauseCircle, label: t("pause"), to: "/app/subscriptions" },
              { icon: Plus, label: t("add_sub"), to: "/app/products" },
              { icon: MapPin, label: t("address"), to: "/app/profile" },
              { icon: Sparkles, label: t("offers"), to: "/app/reels" },
            ].map((a, i) => (
              <Link key={i} to={a.to} className="bg-white rounded-2xl border border-neutral-200 p-3 flex flex-col items-center gap-2 card-hover" data-testid={`quick-${i}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${primary}1A`, color: primary }}>
                  <a.icon className="w-5 h-5" />
                </div>
                <div className="text-xs font-bold text-center">{a.label}</div>
              </Link>
            ))}
          </div>
        )}

        {/* Per-category product strips */}
        {cats.map((c) => (
          <CategoryStrip key={c.id} category={c} products={productsByCategory[c.id]}
                         subcats={subcatsByParent[c.id] || []} primary={primary}/>
        ))}

        {/* Brand strip — clickable chips that filter products list */}
        {data.brands?.length > 0 && (
          <section data-testid="brand-strip">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-heading text-lg font-bold">{t("shop_by_brand")}</h3>
                <div className="text-xs text-neutral-500">{data.brands.length} brand{data.brands.length === 1 ? "" : "s"}</div>
              </div>
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-5 px-5 pb-2">
              {data.brands.map((b) => (
                <Link to={`/app/products?brand=${b.slug}`}
                      key={b.id}
                      className="shrink-0 w-32 bg-white rounded-2xl border border-neutral-200 overflow-hidden card-hover"
                      data-testid={`brand-chip-${b.slug}`}>
                  <div className="aspect-[4/3] bg-brand-cream flex items-center justify-center">
                    {b.logo_url ? (
                      <img src={b.logo_url} alt={b.name} className="w-full h-full object-contain p-3"/>
                    ) : (
                      <div className="font-heading font-extrabold text-2xl" style={{ color: primary }}>{b.name?.[0]}</div>
                    )}
                  </div>
                  <div className="p-2 text-center">
                    <div className="text-xs font-bold truncate">{b.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Uncategorized fallback */}
        {productsByCategory._uncategorized?.length > 0 && (
          <CategoryStrip
            key="_uncategorized"
            category={{ id: "_unc", name: t("shop_fresh") || "Featured", slug: "featured" }}
            products={productsByCategory._uncategorized}
            primary={primary}
          />
        )}

        {/* Reels */}
        {data.reels?.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-bold">{t("from_farm")}</h3>
              <Link to="/app/reels" className="text-sm font-bold" style={{ color: primary }} data-testid="see-all-reels">
                {t("see_all")} →
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-5 px-5 pb-2">
              {data.reels.map((r) => (
                <Link to="/app/reels" key={r.id} className="shrink-0 w-40 rounded-2xl overflow-hidden relative bg-brand-ink h-60" data-testid={`reel-card-${r.id}`}>
                  <img src={r.thumbnail_url} alt={r.title} className="absolute inset-0 w-full h-full object-cover opacity-80" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2 text-white">
                    <div className="text-sm font-bold leading-tight line-clamp-2">{r.title}</div>
                    <div className="flex items-center gap-3 mt-1 text-xs">
                      <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3"/>{r.likes}</span>
                      <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3"/>{r.views}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="h-4" />
      </main>
    </div>
  );
}

// ===== Helpers =====

function minutesOfDay(hhmm) {
  if (!hhmm || !hhmm.includes(":")) return 0;
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  return h * 60 + m;
}

/**
 * Pick the most-relevant slot to display in the cut-off ribbon.
 *
 * Logic:
 *   - Find first active slot whose cutoff is still in the future today.
 *     → "Order by {cutoff} & we'll deliver {label} {when}, {slot_start} – {slot_end}"
 *     where {when} = "today" (offset=0), "tomorrow" (offset=-1), or "in N days"
 *   - If past every cut-off, pick the first slot and tell the customer to wait
 *     until the next valid delivery date.
 */
function pickActiveSlot(windows) {
  if (!windows || !windows.length) return null;
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const sorted = [...windows].sort(
    (a, b) => minutesOfDay(a.cutoff_time) - minutesOfDay(b.cutoff_time)
  );
  const upcoming = sorted.find((w) => minutesOfDay(w.cutoff_time) > nowMin);
  const deliveryWhen = (offset) => {
    const n = parseInt(offset ?? -1, 10) || 0;
    if (n === 0) return "today";
    if (n === -1) return "tomorrow";
    return `in ${Math.abs(n)} days`;
  };
  if (upcoming) {
    return {
      ...upcoming,
      message: <>Order by <b>{formatClock(upcoming.cutoff_time)}</b> &amp; we&apos;ll deliver <b>{upcoming.label}</b> <b>{deliveryWhen(upcoming.cutoff_day_offset)}</b>, <b>{formatSlotWindow(upcoming)}</b></>,
    };
  }
  // Past all cut-offs — next delivery shifts one day past the latest offset
  const first = sorted[0];
  const off = parseInt(first.cutoff_day_offset ?? -1, 10) || 0;
  const dayLabel = off === 0 ? "tomorrow" : off === -1 ? "the day after tomorrow" : `in ${Math.abs(off) + 1} days`;
  return {
    ...first,
    message: <>Cut-off passed · Orders now will deliver <b>{dayLabel}</b>, <b>{formatSlotWindow(first)}</b></>,
  };
}

function BannerCarousel({ banners, primary, secondary }) {
  const [idx, setIdx] = useState(0);
  const len = banners.length;
  // Auto-advance every 5s (only when >1 banner)
  useEffect(() => {
    if (len <= 1) return;
    const tm = setInterval(() => setIdx((i) => (i + 1) % len), 5000);
    return () => clearInterval(tm);
  }, [len]);

  return (
    <div className="relative rounded-3xl overflow-hidden h-44 sm:h-52" data-testid="banner-carousel">
      {banners.map((b, i) => (
        <BannerSlide key={b.id} banner={b} primary={primary} secondary={secondary}
                     active={i === idx}/>
      ))}
      {len > 1 && (
        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-10">
          {banners.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/50"}`}
                    data-testid={`banner-dot-${i}`}/>
          ))}
        </div>
      )}
    </div>
  );
}

function BannerSlide({ banner: b, active, primary, secondary }) {
  const Inner = (
    <div className={`absolute inset-0 transition-opacity duration-500 ${active ? "opacity-100" : "opacity-0 pointer-events-none"}`}
         style={{ background: b.background_color || `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}
         data-testid={`banner-slide-${b.id}`}>
      {b.image_url && <img src={b.image_url} alt={b.title} className="absolute inset-0 w-full h-full object-cover"/>}
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 70%)" }}/>
      <div className="relative h-full p-5 sm:p-7 flex flex-col justify-center" style={{ color: b.text_color || "#fff" }}>
        {b.subtitle && (
          <div className="text-[11px] uppercase tracking-wider font-bold opacity-90 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3"/> {b.subtitle}
          </div>
        )}
        <div className="font-heading text-2xl sm:text-3xl font-extrabold leading-tight mt-1 max-w-[80%]">
          {b.title}
        </div>
        {b.cta_label && b.cta_link && (
          <div className="mt-3">
            <span className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-2xl bg-white text-sm font-extrabold"
                  style={{ color: b.background_color || primary }}>
              {b.cta_label} <ChevronRight className="w-3.5 h-3.5"/>
            </span>
          </div>
        )}
      </div>
    </div>
  );
  if (b.cta_link?.startsWith("/")) {
    return <Link to={b.cta_link}>{Inner}</Link>;
  }
  if (b.cta_link) {
    return <a href={b.cta_link} target="_blank" rel="noreferrer">{Inner}</a>;
  }
  return Inner;
}

function CategoryCircle({ cat, isActive, primary }) {
  const scrollIntoView = (e) => {
    e.preventDefault();
    const target = document.querySelector(`#cat-${cat.slug}`);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <button onClick={scrollIntoView}
            data-cat-id={cat.id}
            className="shrink-0 flex flex-col items-center gap-2 group transition-transform"
            data-testid={`category-circle-${cat.slug}`}
            data-active={isActive ? "true" : "false"}>
      <div
        className={`relative w-16 h-16 rounded-full overflow-hidden transition-all ${
          isActive ? "ring-4 ring-offset-2 ring-offset-brand-cream scale-110" : "ring-2 ring-offset-2 ring-offset-brand-cream"
        }`}
        style={{ "--tw-ring-color": isActive ? primary : "#e5e5e5" }}>
        {cat.image_url ? (
          <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center font-heading font-extrabold text-white" style={{ background: primary }}>
            {cat.name?.[0]}
          </div>
        )}
      </div>
      <div className={`text-[11px] font-bold text-center leading-tight max-w-[72px] line-clamp-2 transition-colors ${isActive ? "" : "text-neutral-700"}`}
           style={isActive ? { color: primary } : {}}>
        {cat.name}
      </div>
    </button>
  );
}

function CategoryStrip({ category, products, subcats = [], primary }) {
  const [activeSub, setActiveSub] = useState(null);  // null = "All"
  if (!products?.length) return null;

  // Only offer chips for subcategories that actually have a product in
  // this strip — never an empty-feeling chip. For a category with no
  // subcategories (or a tenant with no hierarchy at all) this is always
  // empty, so the chip row never renders and `displayed` is always just
  // `products` — identical to the original behavior.
  const availableSubs = subcats.filter((s) => products.some((p) => p.category_id === s.id));
  const activeSubObj = activeSub ? availableSubs.find((s) => s.id === activeSub) : null;
  const displayed = activeSubObj ? products.filter((p) => p.category_id === activeSubObj.id) : products;
  const seeAllHref = activeSubObj
    ? `/app/products?cat=${category.slug}&sub=${activeSubObj.slug}`
    : `/app/products?cat=${category.slug}`;

  return (
    <section id={`cat-${category.slug}`} className="scroll-mt-44" data-testid={`category-strip-${category.slug}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading text-lg font-bold">{category.name}</h3>
          <div className="text-xs text-neutral-500">{displayed.length} {displayed.length === 1 ? "item" : "items"}</div>
        </div>
        <Link to={seeAllHref} className="text-sm font-bold inline-flex items-center gap-1" style={{ color: primary }} data-testid={`see-all-${category.slug}`}>
          See all <ChevronRight className="w-4 h-4"/>
        </Link>
      </div>
      {availableSubs.length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 pb-3" data-testid={`subcategory-chips-${category.slug}`}>
          <button onClick={() => setActiveSub(null)}
                  className={`px-3 h-8 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${!activeSubObj ? "text-white" : "bg-white border border-neutral-200 text-neutral-700"}`}
                  style={!activeSubObj ? { background: primary } : undefined}
                  data-testid={`sub-chip-all-${category.slug}`}>
            All
          </button>
          {availableSubs.map((s) => (
            <button key={s.id} onClick={() => setActiveSub(s.id)}
                    className={`px-3 h-8 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${activeSubObj?.id === s.id ? "text-white" : "bg-white border border-neutral-200 text-neutral-700"}`}
                    style={activeSubObj?.id === s.id ? { background: primary } : undefined}
                    data-testid={`sub-chip-${s.slug}`}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-5 px-5 pb-2">
        {displayed.slice(0, 8).map((p) => (
          <ProductCard key={p.id} product={p} primary={primary}/>
        ))}
      </div>
    </section>
  );
}

